import {connect} from 'react-redux';
import {useLocation} from 'react-router';
import {GetApp} from '@material-ui/icons';
import {IconButton} from '@material-ui/core';
import {FormattedMessage, useIntl} from 'react-intl';
import React, {useEffect, useState, useMemo} from 'react';

import {Chat} from '../Chat';
import {RootState} from '../../store';
import {TestDetails} from './TestDetails';
import {TestResults} from './TestResults';
import {NoDeployment} from './NoDeployment';
import {ChatConnection} from '../Chat/Chat';
import {getGspList} from '../../api/gsp/GspList';
import {TestDescription} from './TestDescription';
import {Project} from '../../types/project/Project';
import {assertResponseBody} from '../../api/request';
import {getImageSets} from '../../api/test/ImageSets';
import {DeployConfigPanel} from './DeployConfigPanel';
import {toast} from '../../base-components/StudioToast';
import {getDeviceList} from '../../api/device/DeviceList';
import {UnparsedDate} from '../../types/utility/UnparsedDate';
import {renderToObject} from './TestDetails/TestDetailsConfig';
import {getProjectData, projectFetch} from '../../store/project';
import {downloadFile, downloadBlob} from '../../utils';
import {DeploymentDetailsInfo} from '../../types/deployment/DeploymentDetailsInfo';
import {StudioTab, StudioTabPanel, StudioTabs} from '../../base-components/StudioTab';
import {
  DownloadDetailsForm,
  DownloadDetailsDialog,
} from './DownloadDetailsDialog/DownloadDetailsDialog';
import {
  useDeploy,
  setGSPList,
  TASK_STATUS,
  setBaseModel,
  setImageSets,
  setStreamingDevices,
  useDeploymentDetails,
} from './useDeploy';

import './TestDeployView.scss';
import {VideoStreamSubject} from './HlsVideoStream';

export type TestDeployViewProps = {
  initialProjectData: Project;
  projectId: string;
  fetchProjectData: Function;
};

const emptyProject = {
  id: null,
  name: null,
  description: null,
  icon: 'icons/model-icon.png',
  lastUpdateTime: null,
  dataSet: {
    name: '',
    size: 0,
    creationDate: null,
    dataType: null,
    count: 0,
    histogram: null,
    pageCount: 0,
  },
  model: null,
  transformations: {},
  applications: {},
};

type InferenceResultsProps = {
  isONNXIdle: boolean;
  isDeploying: boolean;
  selectedDeploymentIds: string[];
  deployment: DeploymentDetailsInfo | null;
  streamSubject: VideoStreamSubject;
  projectId: string;
  chatConnection?: ChatConnection;
};

function InferenceResults({
  isONNXIdle,
  deployment,
  streamSubject,
  projectId,
  chatConnection,
  isDeploying,
  selectedDeploymentIds,
}: InferenceResultsProps) {
  if (chatConnection) {
    return <Chat connection={chatConnection} />;
  }

  if (selectedDeploymentIds.length > 1) {
    return (
      <NoDeployment message={<FormattedMessage id="test.multipleTestsSelected" />} />
    );
  }

  if (deployment && deployment.deploymentType === 'ONNX_WEB_INFERENCE') {
    return <NoDeployment message={<FormattedMessage id="test.noOnnxResults" />} />;
  }

  if (!deployment && !isDeploying && isONNXIdle) {
    return (
      <>
        <NoDeployment
          imageUrl="images/emptyStateNoResults.png"
          message={<FormattedMessage id="test.resultsWillShow" />}
        />
      </>
    );
  }

  return (
    <TestResults
      deployment={deployment}
      streamSubject={streamSubject}
      isDeploying={isDeploying}
    />
  );
}

type DeploymentDetailsProps = {
  selectedDeploymentIds: string[];
  deployment: DeploymentDetailsInfo | null;
};

function DeploymentDetails({selectedDeploymentIds, deployment}: DeploymentDetailsProps) {
  if (selectedDeploymentIds.length > 1) {
    return (
      <NoDeployment message={<FormattedMessage id="test.multipleTestsSelected" />} />
    );
  }

  if (!deployment) {
    return (
      <NoDeployment
        imageUrl="images/emptyStateNoResults.png"
        message={<FormattedMessage id="test.resultsWillShow" />}
      />
    );
  }

  return (
    <div className="test-deploy-view__details">
      <TestDetails ids={[deployment.id]} hideMissingFields />
    </div>
  );
}

const TestDeployViewBase = ({
  initialProjectData,
  projectId,
  fetchProjectData,
}: TestDeployViewProps) => {
  const intl = useIntl();
  const [
    {deployTask, inferenceTask, onnxTask, selectedDeploymentIds, chatConnection},
    dispatch,
  ] = useDeploy();
  const {search: queryParams} = useLocation();
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedPanelTab, setSelectedPanelTab] = useState(0);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadingCancelPromise, setDownloadingCancelPromise] = useState<() => void>();
  const [isExpanded, setExpanded] = useState(true);
  const [streamSubject, setStreamSubject] = useState<VideoStreamSubject>();

  const projectData = useMemo(
    () =>
      initialProjectData?.id === projectId
        ? initialProjectData
        : ((emptyProject as unknown) as Project),
    [initialProjectData, projectId]
  );
  const firstDeployment = useDeploymentDetails(selectedDeploymentIds?.[0]);
  const deployment = useMemo(
    () => (selectedDeploymentIds.length > 1 ? null : firstDeployment),
    [firstDeployment, selectedDeploymentIds]
  );

  const isDeploying = useMemo(
    () =>
      [deployTask.status, inferenceTask.status, onnxTask.status].includes(
        TASK_STATUS.IN_PROGRESS
      ) || !!chatConnection,
    [chatConnection, deployTask.status, inferenceTask.status, onnxTask.status]
  );

  const baseModel = projectData?.model?.baseModel ?? '';

  useEffect(() => {
    fetchProjectData(projectId);
    setStreamSubject(new VideoStreamSubject());
  }, [projectId, fetchProjectData]);

  // If the user changes to the new test deploy form configuration, then we change the tab to 'test results'
  useEffect(() => {
    if (selectedPanelTab === 0) {
      setSelectedTab(0);
    }
  }, [selectedPanelTab]);

  useEffect(() => {
    const fetchStreamingDevicesList = async () => {
      const devices = await assertResponseBody(getDeviceList());
      if (devices) {
        dispatch(setStreamingDevices(devices));
      }
    };

    fetchStreamingDevicesList();
  }, [dispatch]);

  useEffect(() => {
    // I have no idea how it works, I can only copy and paste stuff
    const fn = async () => {
      const imageSets = await assertResponseBody(getImageSets(projectId));
      if (imageSets) {
        dispatch(setImageSets(imageSets));
      }
    };
    fn();
  }, [dispatch, projectId]);

  const fetchImageSets = async () => {
    const imageSets = await assertResponseBody(getImageSets(projectId));
    if (imageSets) {
      dispatch(setImageSets(imageSets));
    }
  };

  const fetchGspList = async (projectId: string, applicationId: string) => {
    const gspList = await assertResponseBody(getGspList(projectId, applicationId));
    if (gspList) {
      dispatch(setGSPList(gspList));
    }
  };

  useEffect(() => {
    dispatch(setBaseModel(baseModel));
  }, [dispatch, baseModel]);

  useEffect(() => {
    const queryParamsMap = new URLSearchParams(queryParams);
    const initialTestId = queryParamsMap.get('testId');
    if (initialTestId) {
      // dispatch(setSelectedDeploymentIds([initialTestId]));
      setSelectedTab(1);
      setSelectedPanelTab(1);
    }

    // for tabs on page render
    const tab = queryParamsMap.get('tab');
    if (typeof Number(tab) === 'number' && Number(tab) < 2) {
      setSelectedPanelTab(Number(tab));
    } else {
      setSelectedPanelTab(0);
    }
  }, [queryParams, dispatch]);

  async function handleDownload(itemsToDownload: DownloadDetailsForm) {
    if (deployment) {
      const promises = [];
      const cancelFunctions: (() => void)[] = [];

      if (itemsToDownload.inference) {
        let videoInputs = new Set(['WEB_CAMERA', 'IP_CAMERA', 'FILE']);
        let sourceType = deployment.deploymentInput?.inputSourceType || '-';

        if (deployment.inferenceResults && !videoInputs.has(sourceType)) {
          const {promise, cancel} = downloadFile(
            'inferenceResults.json',
            'application/json',
            JSON.stringify(JSON.parse(deployment.inferenceResults), null, 2)
          );

          promises.push(promise);
          cancelFunctions.push(cancel);
        }
      }

      if (itemsToDownload.appDetails) {
        const application = deployment.applicationId
          ? projectData?.applications?.[deployment.applicationId]
          : undefined;
        const detailsBag = {application, project: projectData, test: deployment, intl};
        const details = renderToObject(detailsBag);

        const {promise, cancel} = downloadFile(
          'applicationDetails.json',
          'application/json',
          JSON.stringify(details, null, 2)
        );

        promises.push(promise);
        cancelFunctions.push(cancel);
      }

      if (itemsToDownload.fullApplication) {
        const downloadUrl = `/studio/gsp/download?deploymentId=${deployment.id}&projectId=${projectId}`;
        const {promise, cancel} = downloadBlob({
          bubbleUpError: true,
          fileName: 'application.zip',
          blobUrl: encodeURI(downloadUrl),
        });

        promises.push(
          promise.catch(() => {
            toast.error(intl.formatMessage({id: 'test.errorDownloadingFullApplication'}));
          })
        );
        cancelFunctions.push(cancel);
      }

      setDownloadingCancelPromise(() => () => {
        cancelFunctions.forEach(cancelToken => cancelToken());
      });

      await Promise.all(promises).then(() => {
        setShowDownloadModal(false);
        setDownloadingCancelPromise(undefined);
      });
    }
  }

  let videoInputs = new Set(['WEB_CAMERA', 'IP_CAMERA', 'FILE']);
  let sourceType = deployment?.deploymentInput?.inputSourceType || '-';

  return (
    <div className="test-deploy-view">
      <DownloadDetailsDialog
        isOpen={showDownloadModal}
        onDownload={handleDownload}
        onClose={() => {
          // Cancel content downloading process
          if (downloadingCancelPromise) {
            downloadingCancelPromise();
            setDownloadingCancelPromise(undefined);
          }

          setShowDownloadModal(false);
        }}
        disableInference={!deployment?.inferenceResults || videoInputs.has(sourceType)}
      />
      <div
        className={`test-deploy-view__template test-deploy-view__template--${
          isExpanded ? 'sidebar' : 'no-sidebar'
        }`}
      >
        <TestDescription
          className="test-deploy-view__test-description"
          name={projectData?.name}
          // TODO: correct to project creation time
          createdOn={projectData?.lastUpdateTime as UnparsedDate}
          isExpanded={isExpanded}
          onExpand={() => {
            setExpanded(!isExpanded);
          }}
        />

        <section className="test-deploy-view__tabs">
          <StudioTabs
            className="test-deploy-view__tabs"
            value={selectedTab}
            onChange={(_event, value) => {
              setSelectedTab(value);
            }}
          >
            <StudioTab
              disabled={!Boolean(deployment)}
              label={intl.formatMessage({id: 'test.results'})}
              className="test-deploy-view__tab"
            />
            <StudioTab
              disabled={!Boolean(deployment)}
              label={intl.formatMessage({id: 'test.details'})}
              className="test-deploy-view__tab"
            />
          </StudioTabs>
          {deployment && (
            <IconButton
              className="test-deploy-view__download"
              onClick={() => setShowDownloadModal(true)}
            >
              <GetApp />
            </IconButton>
          )}
        </section>

        {streamSubject && (
          <>
            <DeployConfigPanel
              className="test-deploy-view__deploy-config-pa nel"
              shouldBeVisible={isExpanded}
              projectData={projectData}
              projectId={projectId}
              applications={Object.values(projectData?.applications ?? [])}
              isDeploying={isDeploying}
              fetchProjectData={fetchProjectData}
              selectedTab={selectedPanelTab}
              fetchImageSets={fetchImageSets}
              fetchGspList={fetchGspList}
              streamSubject={streamSubject}
              onTabChange={newTab => {
                setSelectedPanelTab(newTab);

                if (newTab === 0) {
                  setSelectedTab(0);
                }
              }}
            />

            <section className="test-deploy-view__results">
              <StudioTabPanel
                className="test-deploy-view__tab-panel"
                value={selectedTab}
                index={0}
              >
                <InferenceResults
                  deployment={deployment}
                  projectId={projectId}
                  streamSubject={streamSubject}
                  chatConnection={chatConnection}
                  isDeploying={isDeploying}
                  selectedDeploymentIds={selectedDeploymentIds}
                  isONNXIdle={onnxTask.status === TASK_STATUS.IDLE}
                />
              </StudioTabPanel>
              <StudioTabPanel
                className="test-deploy-view__tab-panel"
                value={selectedTab}
                index={1}
              >
                <DeploymentDetails
                  deployment={deployment}
                  selectedDeploymentIds={selectedDeploymentIds}
                />
              </StudioTabPanel>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  initialProjectData: getProjectData(state),
});

const mapDispatchToProps = {
  fetchProjectData: projectFetch,
};

export const TestDeployView = connect(
  mapStateToProps,
  mapDispatchToProps
)(TestDeployViewBase);
